import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bulkDeleteDocumentWorkflowTemplates } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { BulkActionPayloadDto } from '@dealstack/shared/lib/generated';
import { useMasterDocumentsEndpoints } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentsEndpoints.ts';

export const useBulkDeleteMasterDocuments = (onSuccess: () => void) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const { list: listKey, bulkDelete } = useMasterDocumentsEndpoints();

	const mutationFn = (params: BulkActionPayloadDto) =>
		bulkDeleteDocumentWorkflowTemplates(bulkDelete, params);

	return useMutation({
		mutationFn: async (params: BulkActionPayloadDto) => {
			return await mutationFn(params);
		},
		onSuccess: async (result) => {
			await queryClient.invalidateQueries({ queryKey: [listKey] });
			onSuccess?.();

			if (result.failedCount > 0 && result.deletedCount > 0) {
				showToastV2({
					state: 'warning',
					title: tMipManage('master_documents.toast.bulk_delete_partial', {
						deletedCount: result.deletedCount,
						failedCount: result.failedCount,
					}),
				});
			} else if (result.failedCount > 0 && result.deletedCount === 0) {
				showToastV2({
					state: 'error',
					title: tMipManage('master_documents.toast.bulk_delete_all_failed', {
						failedCount: result.failedCount,
					}),
				});
			} else {
				showToastV2({
					title: tMipManage('master_documents.toast.bulk_delete', {
						count: result.deletedCount,
					}),
				});
			}
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('master_documents.toast.bulk_operation_error'),
			});
		},
	});
};
